import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  type GlobalSearchHit,
  type GlobalSearchSection,
  SearchAddressesDocument,
  SearchIssuesDocument,
  SearchTeamsDocument,
  SearchUsersDocument,
  type SearchHitKind,
} from "#app/lib/data";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import type { Omittable } from "@salestrak/ui";

// Rows a section shows per page; "load more" grows that section's limit by this
// and refetches only that section's query.
const PAGE = 5;
const DEBOUNCE_MS = 300;

/** Searchable kinds — one independent query each. The server's three hit
 *  kinds plus `address`, which is a Melissa lookup rather than a search hit. */
export type SearchKind = SearchHitKind | "address";

/**
 * One kind's section (a page of items plus the total match count) and one hit
 * inside it. Both are row aliases over the search fragments — all four kind
 * queries return the shape identically, so neither is named for one of them
 * or reached by an extraction (R3).
 */
export type { GlobalSearchHit, GlobalSearchSection };

/** Sections keyed by kind (the shape consumers render). */
export interface GlobalSearchResults {
  issues: GlobalSearchSection;
  users: GlobalSearchSection;
  teams: GlobalSearchSection;
  addresses: GlobalSearchSection;
}

type UseGlobalSearchOptions = Omittable<{
  /** Limit issue hits to this workflow (contextual scope). */
  scopeWorkflowId?: string;
  /**
   * Restrict which kinds are fetched (the active filter chips). Empty/undefined
   * = fetch every kind. A kind that isn't wanted is never queried, so filtering
   * to e.g. "People" costs zero I/O for issues/teams and — importantly — never
   * fires the paid Google Places (addresses) call.
   */
  kinds?: SearchKind[];
}>;

const EMPTY_SECTION: GlobalSearchSection = { __typename: "GlobalSearchSection", items: [], total: 0 };

/** Maps a searchable kind to its section key in {@link GlobalSearchResults}.
 *  Exported as the single source of truth — consumers import this rather than
 *  re-declaring their own (a drift silently routes a section to the wrong data).
 *  It's a `Record<SearchKind, …>`, so adding a kind is a compile error until
 *  wired here. */
export const KIND_TO_SECTION: Record<SearchKind, keyof GlobalSearchResults> = {
  issue: "issues",
  user: "users",
  team: "teams",
  address: "addresses",
};

/** All searchable kinds, in declaration order. */
export const ALL_KINDS = Object.keys(KIND_TO_SECTION) as SearchKind[];

/**
 * Debounced global search. Each entity kind is its OWN query (searchIssues /
 * searchUsers / searchTeams), so they load and paginate independently — a
 * "load more" on one section refetches only that section. Each returns a page of
 * `items` plus a `total`. `scopeWorkflowId` (contextual scope) limits issue
 * hits; it's optional and off by default.
 */
export function useGlobalSearch(options: UseGlobalSearchOptions = {}) {
  const { scopeWorkflowId, kinds } = options;
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  // A leading "/" is command mode (the surface narrows its command list
  // client-side) — nothing is searched, so no kind query ever fires for it.
  const searchable = trimmed.startsWith("/") ? "" : trimmed;
  const debounced = useDebouncedValue(searchable, DEBOUNCE_MS);
  // Clearing takes effect immediately; only typing is debounced.
  const debouncedQuery = searchable === "" ? "" : debounced;
  const skip = debouncedQuery.length === 0;

  // Empty/undefined kinds = fetch everything; otherwise only the selected kinds.
  const wantKind = (kind: SearchKind) => !kinds?.length || kinds.includes(kind);

  // Each section fetches a fixed PAGE at a time. "Load more" calls `fetchMore`
  // with `offset = rows already shown`, so only the next page is queried and the
  // cache's `searchSectionPagination` merge appends it — prior pages are never
  // re-transferred. A new query text is a distinct cache entry, so paging
  // naturally starts fresh with no manual reset.
  // Variables are memoized (not inline object literals) so each render reuses the
  // same ref — inline `useQuery` variables re-subscribe every render.
  const issuesVars = useMemo(
    () => ({
      query: debouncedQuery,
      limit: PAGE,
      offset: 0,
      scopeWorkflowId: scopeWorkflowId || undefined,
    }),
    [debouncedQuery, scopeWorkflowId],
  );
  const kindVars = useMemo(
    () => ({ query: debouncedQuery, limit: PAGE, offset: 0 }),
    [debouncedQuery],
  );
  const issuesQ = useQuery(SearchIssuesDocument, {
    variables: issuesVars,
    skip: skip || !wantKind("issue"),
    fetchPolicy: "cache-and-network",
  });
  const usersQ = useQuery(SearchUsersDocument, {
    variables: kindVars,
    skip: skip || !wantKind("user"),
    fetchPolicy: "cache-and-network",
  });
  const teamsQ = useQuery(SearchTeamsDocument, {
    variables: kindVars,
    skip: skip || !wantKind("team"),
    fetchPolicy: "cache-and-network",
  });
  const addressesQ = useQuery(SearchAddressesDocument, {
    variables: kindVars,
    skip: skip || !wantKind("address"),
    // Google Places predictions/placeIds are ephemeral — a cached placeId can
    // expire and then resolve to no lat/lng. Never cache them (matching the old
    // address-search hook); always fetch fresh so ResolveAddressSuggestion gets
    // a live placeId.
    fetchPolicy: "no-cache",
  });

  // `previousData` bridges query-text / load-more refetches so a section never
  // flashes empty. For issues it must NOT bridge a scope change: the previous
  // scope's hits include records that don't belong in the new scope (wrong, not
  // merely stale), so on a scope change we show nothing until the new data lands.
  const scopeKey = scopeWorkflowId ?? "";
  const lastIssuesScope = useRef(scopeKey);
  useEffect(() => {
    if (issuesQ.data) lastIssuesScope.current = scopeKey;
  }, [issuesQ.data, scopeKey]);

  // A skipped kind (query text empty, or the kind filtered out) reports an empty
  // section — so its stale `previousData` never leaks into the rendered counts
  // or the footer's context map.
  const skipIssue = skip || !wantKind("issue");
  const skipUser = skip || !wantKind("user");
  const skipTeam = skip || !wantKind("team");
  const skipAddress = skip || !wantKind("address");

  const issues = useMemo<GlobalSearchSection>(() => {
    if (skipIssue) return EMPTY_SECTION;
    const bridge =
      lastIssuesScope.current === scopeKey ? issuesQ.previousData : undefined;
    return (issuesQ.data ?? bridge)?.searchIssues ?? EMPTY_SECTION;
  }, [issuesQ.data, issuesQ.previousData, skipIssue, scopeKey]);

  const users = useMemo<GlobalSearchSection>(
    () =>
      skipUser
        ? EMPTY_SECTION
        : ((usersQ.data ?? usersQ.previousData)?.searchUsers ?? EMPTY_SECTION),
    [usersQ.data, usersQ.previousData, skipUser],
  );

  const teams = useMemo<GlobalSearchSection>(
    () =>
      skipTeam
        ? EMPTY_SECTION
        : ((teamsQ.data ?? teamsQ.previousData)?.searchTeams ?? EMPTY_SECTION),
    [teamsQ.data, teamsQ.previousData, skipTeam],
  );

  const addresses = useMemo<GlobalSearchSection>(
    () =>
      skipAddress
        ? EMPTY_SECTION
        : ((addressesQ.data ?? addressesQ.previousData)?.searchAddresses ??
          EMPTY_SECTION),
    [addressesQ.data, addressesQ.previousData, skipAddress],
  );

  const results = useMemo<GlobalSearchResults>(
    () => ({ issues, users, teams, addresses }),
    [issues, users, teams, addresses],
  );

  // Fetch the next page for one section at `offset = rows already shown`; the
  // cache merge appends it. Only the new page crosses the wire. `fetchMore`
  // reuses the query's other variables, so only `offset` changes.
  const loadMore = useCallback(
    (kind: SearchKind) => {
      // Offset the next page from THIS query's own landed data — never from the
      // rendered section, which may still be bridged `previousData` from the
      // prior query text (a longer, stale list). Using the bridged length would
      // request a page past the fresh entry's end, and the cache merge would
      // clamp/skip rows. While the fresh page is in flight (no own data yet),
      // offset 0 harmlessly re-fetches the first page. Rejections are swallowed —
      // a failed page load leaves the shown rows intact.
      switch (kind) {
        case "issue": {
          const offset = issuesQ.data?.searchIssues.items.length ?? 0;
          void issuesQ.fetchMore({ variables: { offset } }).catch(() => {});
          break;
        }
        case "user": {
          const offset = usersQ.data?.searchUsers.items.length ?? 0;
          void usersQ.fetchMore({ variables: { offset } }).catch(() => {});
          break;
        }
        case "team": {
          const offset = teamsQ.data?.searchTeams.items.length ?? 0;
          void teamsQ.fetchMore({ variables: { offset } }).catch(() => {});
          break;
        }
        case "address": {
          const offset = addressesQ.data?.searchAddresses.items.length ?? 0;
          void addressesQ.fetchMore({ variables: { offset } }).catch(() => {});
          break;
        }
      }
    },
    [issuesQ, usersQ, teamsQ, addressesQ],
  );

  const clear = useCallback(() => setQuery(""), []);

  // A search is "ongoing" while a typed query is still debouncing, or while a
  // section is fetching AND nothing is on screen yet. Because every section uses
  // cache-and-network, each keystroke/load-more triggers a background refetch
  // that flips `.loading` true even with bridged results already rendered —
  // gating on "no results yet" keeps the spinner to the cold load instead of
  // flickering on during normal typing/pagination (see the app AGENTS.md rule:
  // only `loading && !data` should gate visibility).
  // Keyed off `searchable`, not `trimmed` — a slash (command-mode) query never
  // debounces toward a fetch, so it must not read as pending either.
  const debouncePending =
    searchable.length > 0 && searchable !== debouncedQuery;
  const anyFetching =
    issuesQ.loading || usersQ.loading || teamsQ.loading || addressesQ.loading;
  const hasAnyResults =
    issues.items.length > 0 ||
    users.items.length > 0 ||
    teams.items.length > 0 ||
    addresses.items.length > 0;
  const loading = debouncePending || (anyFetching && !hasAnyResults);

  return {
    query,
    setQuery,
    debouncedQuery,
    results,
    loadMore,
    loading,
    clear,
  };
}
